<template>
  <v-sheet class="highlight-tooltip-content w-100 d-flex flex-column ga-2 border rounded pa-3" :class="severityClass">
    <!-- Pages container - all pages rendered but only current visible -->
    <div class="pages-container position-relative">
      <div
        v-for="(tooltip, pageIndex) in tooltipData"
        :key="tooltip.id"
        class="tooltip-page w-100 d-flex flex-column ga-2"
        :class="{ 'is-visible': pageIndex + 1 === currentPage }"
      >
        <div class="w-100 d-flex flex-column ga-2">
          <!-- Category Label -->
          <div class="w-100 d-flex align-center">
            <span class="category-label text-caption text-uppercase">{{ tooltip.category }}</span>
          </div>

          <!-- Title -->
          <div class="text-h2 text-textDarkest">
            <DpplSafeHtml :html="tooltip.title" />
          </div>

          <!-- Description with optional learn more link -->
          <div class="text-body-2 text-textDark">
            <DpplSafeHtml :html="tooltip.description" />
            <a
              v-if="tooltip.showLearnMore"
              class="text-linkDefault text-decoration-underline cursor-pointer"
              @click.prevent="$emit('learn-more-click', tooltip.id)"
            >
              Learn more...
            </a>
          </div>
        </div>

        <!-- Suggestion Buttons -->
        <v-btn
          v-for="(suggestion, index) in tooltip.aiSuggestions?.[tooltip.id] || tooltip.suggestions"
          :key="index"
          variant="outlined"
          :color="getButtonColor(tooltip)"
          class="suggestion-button text-button-3 text-textDarkest"
          rounded="lg"
          height="auto"
          block
          :disabled="tooltip.readOnly"
          @click="$emit('suggestion-click', { id: tooltip.id, suggestion: suggestion.text })"
        >
          <span class="text-break text-wrap ma-2">
            {{ suggestion.text }}
          </span>
        </v-btn>

        <v-btn
          v-if="tooltip.aiSuggest && !tooltip.aiSuggestions?.[tooltip.id]"
          class="ai-suggest-button text-button-3 text-textDarkest"
          :color="getButtonColor(tooltip)"
          rounded="lg"
          height="30"
          elevation="0"
          :disabled="tooltip.readOnly"
          :loading="tooltip.isLoading"
          @click.stop="$emit('ai-suggest-click', tooltip.id)"
        >
          <FirstDraftStarsFilled
            height="20"
            class="mr-1"
          />
          Rewrite with AI
        </v-btn>

        <v-btn
          v-if="tooltip.showApplyTemplate"
          class="apply-template-button text-button-3 text-textDarkest"
          variant="outlined"
          :color="getButtonColor(tooltip)"
          rounded="lg"
          height="30"
          :disabled="tooltip.readOnly"
          @click="$emit('apply-template-click', tooltip.id)"
        >
          Apply Template
        </v-btn>

        <v-btn
          v-if="tooltip.deleteButton"
          variant="outlined"
          :color="getButtonColor(tooltip)"
          class="delete-button text-button-3 text-textDarkest"
          rounded="lg"
          height="auto"
          block
          :disabled="getTooltipData(tooltip).readOnly"
          @click="$emit('delete-click', tooltip.id)"
        >
          <v-icon icon="$delete-outline" />
          <span class="my-2">
            Delete
          </span>
        </v-btn>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="showPagination" class="pagination d-flex align-center justify-center ga-2 w-100 justify-space-between">
      <v-btn
        icon
        variant="text"
        height="30"
        width="30"
        rounded="lg"
        size="small"
        :disabled="currentPage === 1"
        @click.stop="prevPage"
      >
        <v-icon icon="$chevron-left" />
      </v-btn>

      <span class="pagination-text text-body-2 text-textDark">
        {{ currentPage }} / {{ totalPages }}
      </span>

      <v-btn
        icon
        variant="text"
        rounded="lg"
        height="30"
        width="30"
        size="small"
        :disabled="currentPage === totalPages"
        @click.stop="nextPage"
      >
        <v-icon icon="$chevron-right" />
      </v-btn>
    </div>
  </v-sheet>
</template>

<script>
import DpplSafeHtml from '@ui/components/general/DpplSafeHtml/DpplSafeHtml.vue';
import FirstDraftStarsFilled from '@ui/assets/icons/First_Draft_Stars_Filled.svg?component';

export default {
  name: 'HighlightTooltipContent',

  components: { DpplSafeHtml, FirstDraftStarsFilled },

  props: {
    tooltips: {
      type: Array,
      required: true,
      validator: (value) => {
        return value.every(item =>
          typeof item === 'object' &&
          item.id !== undefined &&
          item.category !== undefined &&
          item.title !== undefined &&
          item.description !== undefined &&
          item.readOnly !== undefined &&
          item.aiSuggest !== undefined &&
          item.aiSuggestions !== undefined &&
          item.showApplyTemplate !== undefined &&
          item.deleteButton !== undefined &&
          item.showLearnMore !== undefined &&
          item.suggestions !== undefined &&
          item.severity !== undefined &&
          item.isLoading !== undefined
        );
      },
    },
  },

  emits: ['suggestion-click', 'learn-more-click', 'delete-click', 'ai-suggest-click', 'apply-template-click'],

  data() {
    return {
      currentPage: 1,
    };
  },

  computed: {
    totalPages() {
      return this.tooltips.length;
    },

    showPagination() {
      return this.totalPages > 1;
    },

    currentTooltip() {
      const tooltip = this.tooltips[this.currentPage - 1] || {};
      return this.getTooltipData(tooltip);
    },

    severityClass() {
      return `severity-${this.currentTooltip.category}`;
    },

    tooltipData() {
      return this.tooltips.map((tooltip) => this.getTooltipData(tooltip));
    },
  },

  watch: {
    tooltips: {
      handler() {
        // Reset to first page when tooltips change
        if (this.currentPage > this.totalPages) {
          this.currentPage = Math.max(1, this.totalPages);
        }
      },
      deep: true,
    },
  },

  methods: {
    getTooltipData(tooltip) {
      return {
        id: tooltip.id,
        category: tooltip.category || '',
        title: tooltip.title || '',
        description: tooltip.description || '',
        showLearnMore: tooltip.showLearnMore ?? false,
        suggestions: tooltip.suggestions || [],
        severity: tooltip.severity || 'performance',
        deleteButton: tooltip.deleteButton ?? false,
        aiSuggest: tooltip.aiSuggest ?? false,
        showApplyTemplate: tooltip.showApplyTemplate ?? false,
        readOnly: tooltip.readOnly ?? false,
        isLoading: tooltip.isLoading ?? false,
        aiSuggestions: tooltip.aiSuggestions,
      };
    },

    getButtonColor(tooltip) {
      const tooltipData = this.getTooltipData(tooltip);
      const colorMap = {
        performance: 'red-500',
        brand: 'blue-500',
        compliance: 'yellow-700',
        kudos: 'green-500',
      };
      return colorMap[tooltipData.severity] || 'red-500';
    },

    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },

    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },
  },
};
</script>

<style lang="less" scoped>
@categoryBorders: {
  performance: @solidBorders[red];
  brand: @solidBorders[blue];
  compliance: @solidBorders[yellow];
  kudos: @solidBorders[green];
};

@categoryBackgrounds: {
  performance: @solidBackgrounds[red];
  brand: @solidBackgrounds[blue];
  compliance: @solidBackgrounds[yellow];
  kudos: @solidBackgrounds[green];
};

.highlight-tooltip-content {
  each(@categoryBorders, .(@color, @key) {
    &.severity-@{key} {
      border-color: @color !important;
      .category-label {
        color: @color;
      }
      :deep(.v-btn--variant-outlined) {
        border-color: @color;
      }
      :deep(.v-btn--elevated) {
        background-color: @color;
        color: @white !important;
      }
    }
  });

  each(@categoryBackgrounds, .(@color, @key) {
    &.severity-@{key} {
      :deep(.v-btn--variant-outlined) {
        &:hover {
          background-color: @color;
          border-color: @color;
        }
      }
      :deep(.v-btn--elevated) {
        &:hover {
          background-color: @color;
          color: inherit !important;
        }
      }
    }
  });

  .ai-suggest-button {
    :deep(svg) {
      path {
        fill: @white !important;
      }
    }
    &:hover {
      :deep(svg) {
        path {
          fill: initial !important;
        }
      }
    }
  }

  .pages-container {
    // Use grid to stack all pages in the same cell
    display: grid;

    .tooltip-page {
      // All pages occupy the same grid cell, stacking them
      grid-area: 1 / 1;

      // Hide all pages by default
      opacity: 0;
      pointer-events: none;
      visibility: hidden;

      // Only show the current page
      &.is-visible {
        opacity: 1;
        pointer-events: auto;
        visibility: visible;
      }
    }
  }

  .pagination {
    .pagination-text {
      min-width: 40px;
      text-align: center;
    }
  }
}
</style>
